import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FieldSpecsLayout } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { FormControl, FormGroup } from "@angular/forms";
import { INumberFieldSpecs } from "libraries/module/formbuilder/fields/number/INumberFieldSpecs";
import { ISimpleSelectFieldConfig } from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { VatConfigurationWording } from "./vat-configuration-wording";
import { IQuotationVatConfiguration } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { EVatDisplayMode } from "libraries/service/vat/vat.interface";
import { NumberComponent } from 'libraries/module/formbuilder/fields/number/number.component';

const VAT_RATE_NAME: string = "vatRate";

@Component({
    selector: 'vat-configuration-form',
    template: `
        <div class="vat-configuration"
             [class.horizontal]="!menuLayout"
             [class.vertical]="menuLayout"
             [formGroup]="myFormGroup">
            <number #vatRateField
                    class="vat-rate"
                    [fieldConfig]="vatRateFieldConfig"
                    [showPlusAndMinus]="false"
                    (onChange)="updateVatDisplayModeControl($event)"
                    (onKeyup)="onVatRateKeyUp()"
                    [emitKeys]="null"></number>
            <simple-select class="vat-display-mode"
                           [withBorder]="true"
                           [fieldConfig]="vatDisplayModeFieldConfig"></simple-select>
        </div>
    `,
    styles: [`
        .horizontal {
            display: flex;
        }
        .horizontal * {
            margin-right: 1.5rem;
        }

        .vertical {
            display: block;
        }
        .vertical * {
            margin-bottom: 0.75rem;
        }

        .vat-rate {
            width: 7rem;
        }

        .vat-display-mode {
            min-width: 15rem;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VatConfigurationFormComponent implements OnInit, OnChanges {

    /** Optional. Specify your own form if you need to handle validation. */
    @Input()
    public myFormGroup: FormGroup = new FormGroup({});

    /** Optional. Default is `false`, horizontal layout. If `true`, menu layout (vertical). */
    @Input()
    public menuLayout: boolean = false;

    /** Optional. Omit `vatConfiguration` for a new quotation. Specify a `vatConfiguration` for an existing quotation. */
    @Input()
    public vatConfiguration?: IQuotationVatConfiguration = { vatRate: 0, vatDisplayMode: null };

    @ViewChild('vatRateField')
    public vatRateField: NumberComponent;

    public vatRateFieldConfig: INumberFieldSpecs;
    public vatDisplayModeFieldConfig: ISimpleSelectFieldConfig;

    /** In this FormControl, vatRate is an int. 20% --> 20 */
    private readonly vatRateControl: FormControl = new FormControl();
    private readonly vatDisplayModeControl: FormControl = new FormControl();

    private readonly wording: VatConfigurationWording = new VatConfigurationWording();

    public ngOnInit(): void {
        this.initVatRateField();
        this.initVatDisplayModeField();

        this.updateControlsAccordingToVatConfiguration();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.vatConfiguration && !changes.vatConfiguration.isFirstChange()) {
            this.updateControlsAccordingToVatConfiguration();
        }
    }

    public updateVatDisplayModeControl(vatRateAsInt: number): void {
        this.vatDisplayModeFieldConfig.disabled = !vatRateAsInt && !isNaN(vatRateAsInt);

        if (!vatRateAsInt) {
            this.vatDisplayModeControl.patchValue(null);
        } else if (!this.vatDisplayModeControl.value) {
            this.vatDisplayModeControl.patchValue(this.vatConfiguration.vatDisplayMode || EVatDisplayMode.includingVAT);
        }
    }

    public onVatRateKeyUp(): void {
        this.updateVatDisplayModeControl(this.vatRateField.getCurrentValue());
    }

    public getUpdatedVatConfiguration(): IQuotationVatConfiguration {
        return { vatRate: this.getUpdatedVatRate(), vatDisplayMode: this.getUpdatedVatDisplayMode() };
    }

    private getUpdatedVatRate(): number {
        return intRateToFloatRate(this.vatRateControl.value);
    }

    private getUpdatedVatDisplayMode(): EVatDisplayMode {
        return this.vatDisplayModeControl.value;
    }

    private initVatRateField(): void {
        this.myFormGroup.addControl(VAT_RATE_NAME, this.vatRateControl);
        this.vatRateFieldConfig = {
            formControlName: VAT_RATE_NAME,
            formGroup: this.myFormGroup,
            label: this.wording.vatRate,
            min: 0,
            max: 100,
            step: 0.01,
            unit: '%',
            layout: FieldSpecsLayout.block,
            fixed: 2,
            withGreyLabel: this.menuLayout,
        };
    }

    private initVatDisplayModeField(): void {
        const vatDisplayMode: string = "vatDisplayMode";
        this.myFormGroup.addControl(vatDisplayMode, this.vatDisplayModeControl);
        this.vatDisplayModeFieldConfig = {
            formControlName: vatDisplayMode,
            formGroup: this.myFormGroup,
            label: this.wording.vatDisplayMode,
            options: [
                { value: EVatDisplayMode.excludingVAT, label: this.wording.excludedTax },
                { value: EVatDisplayMode.includingVAT, label: this.wording.includedTax }
            ],
            withGreyLabel: this.menuLayout,
            description: {
                main: this.wording.displayModeHelp,
            }
        };
    }

    private updateControlsAccordingToVatConfiguration(): void {
        const vatRateAsInt: number = floatRateToIntRate(this.vatConfiguration.vatRate) || 0;
        this.vatRateControl.patchValue(vatRateAsInt);
        this.updateVatDisplayModeControl(vatRateAsInt);
    }
}

function floatRateToIntRate(floatRate: number): number {
    return floatRate && (floatRate * 100);
}

function intRateToFloatRate(intRate: number): number {
    return intRate && (intRate / 100);
}
